/**
 * Vertically centers floating carousel controls on a target within the active slide
 * (e.g. .product-card__figure). Add data-align-controls-to=".product-card__figure"
 * on a .floating-controls-container that wraps scroll-carousel + controls.
 */
const initialized = new WeakSet();

function getAlignTarget(container) {
  const targetSelector = container.getAttribute("data-align-controls-to");
  const carousel = container.querySelector("scroll-carousel");

  if (!targetSelector || !carousel) {
    return null;
  }

  const cell =
    carousel.selectedCell ??
    carousel.querySelector("product-card, .product-card, .collection-card");

  return cell?.matches?.(targetSelector) ? cell : cell?.querySelector(targetSelector);
}

function alignControls(container) {
  const carousel = container.querySelector("scroll-carousel");
  const controls = container.querySelectorAll(".floating-controls-container__control");
  const target = getAlignTarget(container);

  if (!target || !controls.length) {
    return false;
  }

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const top = targetRect.top + targetRect.height / 2 - containerRect.top;
  const outsideGap = container.getAttribute("data-align-controls-outside");
  const outsideOffset = outsideGap
    ? /(?:rem|px|%)$/i.test(outsideGap.trim())
      ? outsideGap.trim()
      : `${parseFloat(outsideGap) || 10}px`
    : null;
  const outsideFrom = parseFloat(container.getAttribute("data-align-controls-outside-from")) || 700;
  const insideInsetAttr = container.getAttribute("data-align-controls-inside-inset");
  const insideInset = insideInsetAttr !== null ? insideInsetAttr : "0";
  const useOutsideOffset = outsideOffset && window.matchMedia(`(min-width: ${outsideFrom}px)`).matches;

  controls.forEach((control, index) => {
    control.style.insetBlockStart = `${top}px`;

    if (useOutsideOffset) {
      if (index === 0) {
        control.style.insetInlineStart = "0";
        control.style.insetInlineEnd = "auto";
        control.style.transform = `translate(calc(-100% - ${outsideOffset}), -50%)`;
      } else if (index === controls.length - 1) {
        control.style.insetInlineStart = "auto";
        control.style.insetInlineEnd = "0";
        control.style.transform = `translate(calc(100% + ${outsideOffset}), -50%)`;
      }
    } else if (outsideOffset) {
      if (index === 0) {
        control.style.insetInlineStart = insideInset;
        control.style.insetInlineEnd = "auto";
      } else if (index === controls.length - 1) {
        control.style.insetInlineStart = "auto";
        control.style.insetInlineEnd = insideInset;
      }

      control.style.transform = "translateY(-50%)";
    } else {
      control.style.insetInlineStart = "";
      control.style.insetInlineEnd = "";
      control.style.transform = "translateY(-50%)";
    }
  });

  return true;
}

function scheduleAlign(container, attempt = 0) {
  requestAnimationFrame(() => {
    const aligned = alignControls(container);

    if (!aligned && attempt < 8) {
      scheduleAlign(container, attempt + 1);
    }
  });
}

function initContainer(container) {
  if (initialized.has(container)) {
    return;
  }

  const carousel = container.querySelector("scroll-carousel");

  if (!carousel) {
    return;
  }

  initialized.add(container);

  const update = () => scheduleAlign(container);

  update();

  const resizeObserver = new ResizeObserver(update);
  resizeObserver.observe(container);
  resizeObserver.observe(carousel);

  carousel.addEventListener("carousel:change", update);
  carousel.addEventListener("carousel:settle", update);
  const outsideFrom = parseFloat(container.getAttribute("data-align-controls-outside-from")) || 700;
  window.matchMedia(`(min-width: ${outsideFrom}px)`).addEventListener("change", update);

  carousel.querySelectorAll("img").forEach((img) => {
    if (!img.complete) {
      img.addEventListener("load", update, { once: true });
    }
  });
}

function init(root = document) {
  root.querySelectorAll("[data-align-controls-to]").forEach(initContainer);
}

function scanForAlignContainers(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  if (node.matches?.("[data-align-controls-to]")) {
    initContainer(node);
  }

  node.querySelectorAll?.("[data-align-controls-to]").forEach(initContainer);
}

function observeDynamicContainers() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(scanForAlignContainers);
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    init();
    observeDynamicContainers();
  });
} else {
  init();
  observeDynamicContainers();
}

document.addEventListener("shopify:section:load", (event) => {
  init(event.target);
});
